import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { CONTACT, KEY_TOWNS } from "../lib/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "Property Maintenance",
  "Garden Maintenance",
  "Clearance & Removal",
  "Seasonal Services",
  "Landlord / Letting Agent",
  "Something else",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    location: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const setField = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and your message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Thanks — we'll be in touch soon.");
      setForm({
        name: "",
        email: "",
        phone: "",
        service: "",
        location: "",
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong sending your enquiry. Please try calling us.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Direct contact — huge type */}
      <section
        className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 md:pt-32 pb-16 fade-up"
        data-testid="contact-hero"
      >
        <p className="overline text-neutral-500 mb-6">Get in touch</p>
        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight max-w-4xl">
          Tell us about
          <span className="block italic">your property.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-neutral-600 text-lg leading-relaxed">
          Fill in the form and it will be emailed straight to us at{" "}
          <span className="text-black">{CONTACT.email}</span>. Prefer to talk?
          The kettle&rsquo;s usually on.
        </p>
      </section>

      {/* Grid: contact info + form */}
      <section className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 fade-up">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Left: details */}
          <aside className="md:col-span-5">
            <div className="space-y-10">
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                data-testid="contact-phone-block"
                className="group block border-t border-black/10 pt-8"
              >
                <div className="flex items-center gap-3 overline text-neutral-500">
                  <Phone size={14} /> Phone
                </div>
                <p className="mt-4 font-serif-display text-4xl sm:text-5xl group-hover:opacity-70 transition-opacity">
                  {CONTACT.phone}
                </p>
              </a>

              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="contact-email-block"
                className="group block border-t border-black/10 pt-8"
              >
                <div className="flex items-center gap-3 overline text-neutral-500">
                  <Mail size={14} /> Email
                </div>
                <p className="mt-4 font-serif-display text-2xl sm:text-3xl break-all group-hover:opacity-70 transition-opacity">
                  {CONTACT.email}
                </p>
              </a>

              <div
                className="border-t border-black/10 pt-8"
                data-testid="contact-coverage-block"
              >
                <div className="flex items-center gap-3 overline text-neutral-500">
                  <MapPin size={14} /> Coverage
                </div>
                <p className="mt-4 font-serif-display text-2xl sm:text-3xl leading-tight">
                  All across Norfolk, plus parts of Cambridgeshire &amp;
                  Lincolnshire.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {KEY_TOWNS.map((t) => (
                    <span
                      key={t}
                      className="text-[11px] tracking-[0.14em] uppercase border border-black/20 px-3 py-1.5 text-neutral-700"
                      data-testid={`town-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Right: form */}
          <div className="md:col-span-7">
            <form
              onSubmit={submit}
              className="border border-black/10 bg-neutral-50/40 p-8 md:p-12 space-y-8"
              data-testid="contact-form"
              noValidate
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field
                  label="Your name"
                  required
                  testId="input-name"
                  value={form.name}
                  onChange={setField("name")}
                />
                <Field
                  label="Email"
                  type="email"
                  required
                  testId="input-email"
                  value={form.email}
                  onChange={setField("email")}
                />
                <Field
                  label="Phone (optional)"
                  type="tel"
                  testId="input-phone"
                  value={form.phone}
                  onChange={setField("phone")}
                />
                <Field
                  label="Town / Location"
                  testId="input-location"
                  value={form.location}
                  onChange={setField("location")}
                />
              </div>

              <div>
                <label className="overline text-neutral-500 block mb-3">
                  Service
                </label>
                <div className="flex flex-wrap gap-2">
                  {SERVICES.map((s) => (
                    <button
                      type="button"
                      key={s}
                      data-testid={`chip-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          service: f.service === s ? "" : s,
                        }))
                      }
                      className={`text-[11px] tracking-[0.16em] uppercase px-4 py-2 border transition-colors ${
                        form.service === s
                          ? "bg-black text-white border-black"
                          : "border-black/20 text-neutral-700 hover:border-black"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="overline text-neutral-500 block mb-3">
                  Tell us about the job <span className="text-black">*</span>
                </label>
                <textarea
                  required
                  data-testid="input-message"
                  value={form.message}
                  onChange={setField("message")}
                  rows={6}
                  className="w-full bg-transparent border-b border-black/30 focus:border-black focus:outline-none py-3 text-neutral-900 placeholder:text-neutral-400 resize-none"
                  placeholder="A few lines about the property, timing and what you'd like done…"
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={loading}
                className="inline-flex items-center gap-3 bg-black text-white hover:bg-neutral-800 disabled:bg-neutral-400 transition-colors px-8 py-5 text-[11px] tracking-[0.22em] uppercase font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Enquiry <Send size={14} />
                  </>
                )}
              </button>

              <p className="text-[11px] text-neutral-500">
                We reply within 1 working day. Emergencies — please call us
                directly.
              </p>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, type = "text", required, value, onChange, testId }) {
  return (
    <div>
      <label className="overline text-neutral-500 block mb-3">
        {label} {required && <span className="text-black">*</span>}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="w-full bg-transparent border-b border-black/30 focus:border-black focus:outline-none py-3 text-neutral-900"
      />
    </div>
  );
}
